import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Star, Quote } from 'lucide-react';
import axios from 'axios';

const mockTestimonials = [
  {
    id: '1',
    name: 'Sarah Johnson',
    company: 'TechCorp Inc',
    role: 'CEO',
    content: 'FuturePath Technologies transformed our digital presence completely. Their team delivered a stunning website that increased our conversion rate by 150%. Highly recommended!',
    rating: 5,
    avatar_url: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=200',
    featured: true,
  },
  {
    id: '2',
    name: 'Michael Chen',
    company: 'E-Shop Plus',
    role: 'Founder',
    content: 'The e-commerce platform they built for us handles thousands of orders daily without any issues. The team is professional, responsive, and truly understands business needs.',
    rating: 5,
    avatar_url: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=200',
    featured: true,
  },
  {
    id: '3',
    name: 'Emily Rodriguez',
    company: 'Healthcare Solutions',
    role: 'CTO',
    content: 'Working with FuturePath was seamless. They delivered our hospital management system on time and within budget. The quality of code and attention to detail is exceptional.',
    rating: 5,
    avatar_url: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?q=80&w=200',
    featured: true,
  },
  {
    id: '4',
    name: 'David Thompson',
    company: 'FinTech Innovations',
    role: 'Product Manager',
    content: 'Our mobile banking app has been a huge success thanks to FuturePath. They understood our security requirements and delivered a robust, user-friendly solution.',
    rating: 5,
    avatar_url: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?q=80&w=200',
    featured: false,
  },
  {
    id: '5',
    name: 'Lisa Anderson',
    company: 'Real Estate Pro',
    role: 'Marketing Director',
    content: 'The SEO work done by FuturePath doubled our organic traffic in just 3 months. Their digital marketing strategies are data-driven and highly effective.',
    rating: 5,
    avatar_url: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?q=80&w=200',
    featured: false,
  },
  {
    id: '6',
    name: 'James Wilson',
    company: 'Startup Ventures',
    role: 'Co-Founder',
    content: 'As a startup, we needed a partner who could move fast without compromising quality. FuturePath exceeded our expectations in every way.',
    rating: 5,
    avatar_url: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=200',
    featured: false,
  },
];

export default function TestimonialsPage() {
  const [testimonials, setTestimonials] = useState(mockTestimonials);

  const renderStars = (rating) => {
    return Array.from({ length: 5 }).map((_, index) => (
      <Star
        key={index}
        size={18}
        className={index < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-600'}
      />
    ));
  };

  return (
    <>
      <Helmet>
        <title>Testimonials - FuturePath Technologies</title>
        <meta name="description" content="Read what our clients say about working with FuturePath Technologies. Real reviews from real businesses." />
      </Helmet>

      <section className="relative min-h-[40vh] flex items-center justify-center pt-32 pb-20">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-secondary/10 to-transparent opacity-50" />
        <div className="container-custom relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold font-outfit mb-6 text-white" data-testid="testimonials-hero-title">
              Client Testimonials
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              Hear from businesses we've helped transform
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section-padding" data-testid="testimonials-grid-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`testimonial-${index}`}
              >
                <div className="glass-card p-8 rounded-3xl h-full flex flex-col">
                  <Quote size={40} className="text-primary/30 mb-4" />
                  
                  <p className="text-muted-foreground text-base leading-relaxed mb-6 flex-grow">
                    "{testimonial.content}"
                  </p>

                  <div className="flex gap-1 mb-4">
                    {renderStars(testimonial.rating)}
                  </div>

                  <div className="flex items-center gap-4">
                    <img
                      src={testimonial.avatar_url}
                      alt={testimonial.name}
                      className="w-14 h-14 rounded-full object-cover ring-2 ring-primary/20"
                    />
                    <div>
                      <h3 className="text-white font-semibold font-outfit">
                        {testimonial.name}
                      </h3>
                      <p className="text-muted-foreground text-sm">
                        {testimonial.role}, {testimonial.company}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-surface/30" data-testid="stats-section">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
            >
              <div className="text-5xl font-bold font-outfit text-primary mb-2">98%</div>
              <div className="text-muted-foreground">Client Satisfaction</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="text-5xl font-bold font-outfit text-primary mb-2">200+</div>
              <div className="text-muted-foreground">Happy Clients</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="text-5xl font-bold font-outfit text-primary mb-2">500+</div>
              <div className="text-muted-foreground">Projects Completed</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <div className="text-5xl font-bold font-outfit text-primary mb-2">5/5</div>
              <div className="text-muted-foreground">Average Rating</div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}
